import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { EMP_STORAGE_KEYS } from "../config";
import {
  clearEmpAuthStorage,
  EMP_UNAUTHORIZED_EVENT,
  setEmpTokens,
} from "../api/client";
import { getMeApi, loginWebApi } from "../api/auth.api";

const EmployeeAuthContext = createContext(null);

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(EMP_STORAGE_KEYS.USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const EmployeeAuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  const [accessToken, setAccessToken] = useState(
    () => localStorage.getItem(EMP_STORAGE_KEYS.ACCESS_TOKEN) || ""
  );
  const [booting, setBooting] = useState(true);

  const applySession = useCallback((data) => {
    // data: { user, accessToken, refreshToken }
    setEmpTokens(data.accessToken, data.refreshToken);
    localStorage.setItem(EMP_STORAGE_KEYS.USER, JSON.stringify(data.user));
    setUser(data.user);
    setAccessToken(data.accessToken);
  }, []);

  const logout = useCallback(() => {
    clearEmpAuthStorage();
    setUser(null);
    setAccessToken("");
  }, []);

  const login = useCallback(
    async (phone, password) => {
      const data = await loginWebApi(phone, password);
      applySession(data);
      return data;
    },
    [applySession]
  );

  // Khởi động: nếu có token, xác thực lại bằng getMe (cập nhật thông tin mới nhất)
  useEffect(() => {
    let active = true;
    (async () => {
      const token = localStorage.getItem(EMP_STORAGE_KEYS.ACCESS_TOKEN);
      if (!token) {
        setBooting(false);
        return;
      }
      try {
        const me = await getMeApi();
        if (!active) return;
        if (me) {
          localStorage.setItem(EMP_STORAGE_KEYS.USER, JSON.stringify(me));
          setUser(me);
        }
      } catch {
        // token hỏng => interceptor sẽ tự xử lý / hoặc để guard đưa về login
      } finally {
        if (active) setBooting(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Nghe sự kiện hết phiên từ axios interceptor
  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener(EMP_UNAUTHORIZED_EVENT, onUnauthorized);
    return () =>
      window.removeEventListener(EMP_UNAUTHORIZED_EVENT, onUnauthorized);
  }, [logout]);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      isAuthenticated: !!accessToken,
      booting,
      login,
      logout,
      setUser,
    }),
    [user, accessToken, booting, login, logout]
  );

  return (
    <EmployeeAuthContext.Provider value={value}>
      {children}
    </EmployeeAuthContext.Provider>
  );
};

export const useEmployeeAuth = () => {
  const ctx = useContext(EmployeeAuthContext);
  if (!ctx)
    throw new Error("useEmployeeAuth phải được dùng trong EmployeeAuthProvider");
  return ctx;
};
